import { treaty } from "@elysiajs/eden";
import type { App } from "@erp/api";
import { isApiErrorBody, type ApiErrorBody } from "@erp/shared";

export const api = treaty<App>(window.location.origin, { fetch: { credentials: "include" } }).api;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody["error"] | null,
  ) {
    super(`API ${status}`);
    this.name = "ApiError";
  }

  get isNetwork() {
    return this.status === 0;
  }
}

interface TreatyResult<T> {
  data: T | null;
  error: { status: unknown; value: unknown } | null;
  status: number;
}

/** Turns an Eden `{ data, error }` pair into data-or-throw, with a typed error. */
export async function unwrap<T>(call: Promise<TreatyResult<T>>): Promise<Exclude<T, ApiErrorBody>> {
  let result: TreatyResult<T>;
  try {
    result = await call;
  } catch {
    throw new ApiError(0, null);
  }
  if (result.error) {
    const value = result.error.value;
    throw new ApiError(result.status, isApiErrorBody(value) ? value.error : null);
  }
  return result.data as Exclude<T, ApiErrorBody>;
}

/**
 * Binary download (zip…), which Eden would read as text. Same contract as `unwrap`: the file, or
 * an ApiError carrying the error code to translate.
 */
export async function fetchFile(path: string, query: Record<string, string>): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`/api${path}?${new URLSearchParams(query)}`, { credentials: "include" });
  } catch {
    throw new ApiError(0, null);
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, isApiErrorBody(body) ? body.error : null);
  }
  return response.blob();
}

/** Same as `fetchFile` for a JSON POST whose reply is a file (previews of unsaved documents). */
export async function postFile(path: string, body: unknown): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, null);
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, isApiErrorBody(payload) ? payload.error : null);
  }
  return response.blob();
}
